<template>
  <div
    :class="{
      [$style.container]: true,
      [$style.active]: isActive,
      [$style.disabled]: isDisable,
    }"
    @click="handleClick"
  >
    <div :class="$style.wrapper">
      <div :class="$style.content">
        <div
          v-if="paymentSystem"
          v-m-r="20"
          :class="[$style.logo, $style[paymentSystem.type]]"
        ></div>

        <div v-overflow:hidden>
          <p
            :class="$style.title"
            v-text="displayCardPan"
          ></p>
          <p
            :class="$style.description"
            v-text="description"
          ></p>
        </div>
      </div>

      <div
        v-if="$scopedSlots.hasOwnProperty('action')"
        :class="$style.action"
      >
        <slot
          name="action"
          :disabled="isDisable"
        ></slot>
      </div>
    </div>

    <div
      v-if="$scopedSlots.hasOwnProperty('additional') && !isDisable"
      v-m-t="10"
    >
      <slot
        name="additional"
        :disabled="isDisable"
      ></slot>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import { isDateAfter } from 'lib/utils/date';
import creditCardType from 'lib/utils/creditCardType';

const AVAILABLE_PAYMENTS_SYSTEMS = [
  'mir',
  'visa',
  'mastercard',
  'maestro',
  'uzcard',
  'humo',
];

export default {
  name: 'SelectableCardPanel',
  props: {
    card: {
      type: Object,
      required: true,
    },
  },
  computed: {
    isActive() {
      if (this.isDisable) {
        return false;
      }

      return this.card.linkedForPayout;
    },
    isDisable() {
      if (this.card.blacklisted) {
        return true;
      }

      // CPME-4433: Для рублевых карт временно не учитываем expireDt
      if (this.isRussianCurrency) {
        return false;
      }

      return isDateAfter(new Date, this.card.expireDt);
    },
    description() {
      if (this.isDisable) {
        return this.$t('expired.or.blocked');
      }

      return this.isActive ? this.$t('used.for.payout') : this.$t('can.choose');
    },
    paymentSystem() {
      // Номер банковской карты
      const { maskedPan } = this.card;

      // Для определение платежной системы необходимо только первые 6 цифр номера карты
      const cardPan = maskedPan.slice(0, 6);

      // Определение платежной системы
      const paymentSystem = creditCardType(cardPan)
        .find(card => AVAILABLE_PAYMENTS_SYSTEMS.includes(card.type));

      if (this.$validate.isUndefined(paymentSystem)) {
        return null;
      }

      return paymentSystem;
    },
    displayCardPan() {
      const { maskedPan } = this.card;

      return maskedPan.replace(/(.{4})(.{2}x{2})(x{4})(\d{4})/i, '$1 $3 $3 $4').replace(/x/gi, '*');
    },
    ...mapGetters(['isRussianCurrency']),
  },
  methods: {
    handleClick() {
      if (this.isActive || this.isDisable) {
        return;
      }

      this.$emit('click');
    },
  },
};
</script>

<style lang="sass" module>
  .container
    padding: 20px
    color: var(--color-white)
    background-color: var(--color-dark-blue-2)
    border-radius: 5px
    box-shadow: var(--box-shadow-base)

    &.active
      background: var(--color-dark-blue-4)

    &.disabled
      color: var(--color-grey-5)
      background-color: var(--color-dark-blue-2)

      .logo
        background-color: var(--color-grey-5)

      .description
        color: var(--color-red)

    &:not(.disabled):not(.active)

      &:hover
        background: var(--color-dark-blue-4)
        cursor: pointer

  .wrapper
    +flexbox
    +flex-direction(column)

    +respond-to('small')
      +flex-direction(row)

  .content
    display: flex
    align-items: center
    min-width: 0

  .logo
    +size(40px)
    flex-shrink: 0
    background-color: var(--color-white)
    background-repeat: no-repeat
    background-position: center
    border-radius: 50%

    &.visa
      background-image: url('~images/payment-system/visa.svg')

    &.mir
      background-image: url('~images/payment-system/mir.svg')

    &.mastercard
      background-image: url('~images/payment-system/mastercard.svg')

    &.maestro
      background-image: url('~images/payment-system/maestro.svg')

    &.uzcard
      background-image: url('~images/payment-system/uzcard.svg')

    &.humo
      background-image: url('~images/payment-system/humo.svg')

  .title
    +font-type(medium, 14, 18)
    overflow-wrap: break-word
    word-wrap: break-word

  .description
    +font-type(regular, 12, 16)
    opacity: 0.5

  .action
    display: none

    +respond-to('small')
      +flexbox
      width: 154px
      margin: 0 0 0 auto
      padding: 0 0 0 5px
</style>
